import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

interface SeasonContextType {
  currentSeason: number;
  setCurrentSeason: (year: number) => void;
  availableSeasons: number[];
  isLoading: boolean;
}

const SeasonContext = createContext<SeasonContextType | undefined>(undefined);

// Default season based on current date
const getDefaultSeason = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth(); // 0-indexed
  
  // If it's after August, use current year for season start
  // Otherwise use previous year
  if (month >= 7) { // August or later
    return year;
  }
  return year - 1;
};

export const SeasonProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [currentSeason, setCurrentSeason] = useState(() => {
    // Check localStorage for saved season
    const saved = localStorage.getItem('selectedSeason');
    return saved ? parseInt(saved) : getDefaultSeason();
  });
  
  const [availableSeasons, setAvailableSeasons] = useState<number[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // Save season selection to localStorage
  useEffect(() => {
    localStorage.setItem('selectedSeason', currentSeason.toString());
  }, [currentSeason]);

  // Load available seasons
  useEffect(() => {
    const loadSeasons = async () => {
      try {
        // For now, just use a range of years
        // Later we can fetch from API
        const years = [];
        const startYear = 1920;
        const endYear = getDefaultSeason() + 1;
        
        for (let year = endYear; year >= startYear; year--) {
          years.push(year);
        }
        
        setAvailableSeasons(years);
      } catch (error) {
        console.error('Failed to load seasons:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadSeasons();
  }, []);

  return (
    <SeasonContext.Provider
      value={{
        currentSeason,
        setCurrentSeason,
        availableSeasons,
        isLoading,
      }}
    >
      {children}
    </SeasonContext.Provider>
  );
};

export const useSeason = () => {
  const context = useContext(SeasonContext);
  if (context === undefined) {
    throw new Error('useSeason must be used within a SeasonProvider');
  }
  return context;
};